import React, { useContext, useEffect, useState } from "react";
import useFormatterCurrency from "../../hooks/useFormatterCurrency";
import { arrayToHtmlList } from "./useFixatServiceForm";
import {
  getServices,
  updateService,
} from "../../apiClient/operations/catalogsOperations";

import { AlertContext } from "../../context/AlertContext";
import StatusMenuButton from "../../tailwindUI/StatusMenuButton";
import usePagination from "../../tailwindUI/usePagination";
import { allowedRolesForRoutes } from "../../routes/SideBarRoutes";
import { AuthContext } from "../../cognito/AuthProvider";

const legalTypeTranslated = {
  personal: "Persona Física",
  enterprise: "Persona Moral",
};

const categoryTranslated = {
  regularization: "Regularización",
  accounting: "Servicios Contables",
};

const timePeriodsTranslated = {
  lt: "Menor",
  lte: "Menor o igual",
  gt: "Mayor",
  gte: "Mayor o igual",
};

const periodicityTranslated = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  bimonthly: "Bimestral",
  unique: "Único",
  yearly: "Anual",
};

const LIMIT_PER_PAGE = 10;

export function useFixatServices({ searchInput = "", filterString = "" }) {
  const [services, setServices] = useState([]);
  const [serviceToEdit, setServiceToEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const { setAlert } = useContext(AlertContext);

  const paginate = usePagination();
  const [pagination, setPagination] = useState(null);

  const { user } = useContext(AuthContext);
  const canEditServices = user.roles.some((role) =>
    allowedRolesForRoutes.editServiceRoles.includes(role)
  );

  const formatService = (service) => {
    const { formatterCurrency } = useFormatterCurrency();
    const tab_discount = Array.isArray(service?.tab_discount)
      ? service?.tab_discount
      : [service?.tab_discount];

    return {
      ...service,
      legal_type_formatted: legalTypeTranslated[service.legal_type] || "",
      category_formatted:
        categoryTranslated[service.category] || service.category,
      cancelation_clauses: arrayToHtmlList(
        service?.service_info?.cancelation_clauses
      ),
      terms_and_conditions: arrayToHtmlList(
        service?.service_info?.terms_and_conditions
      ),
      service_reach: arrayToHtmlList(service?.service_info?.service_reach),
      time_periods_formatted: `${
        timePeriodsTranslated[service.time_periods] || service.time_periods
      }`,
      periodicity_formatted:
        periodicityTranslated[service.periodicity] || service.periodicity,
      price: service.price / 100,
      price_formatted: formatterCurrency(service.price / 100),
      status_formatted: (
        <StatusMenuButton
          status={service.status}
          customStatus={["active", "disabled"]}
          disabled={!canEditServices}
          updateStatus={(newStatus) => {
            if (newStatus === service.status) return;
            setShowModal(true);
            setServiceToEdit({ id: service.id, status: newStatus });
          }}
        />
      ),
      tab_discount:
        service.tab_discount && tab_discount?.length > 0
          ? tab_discount.map((discount) => ({
              ...discount,
              max_discount: discount?.max_discount / 100,
            }))
          : [],
      tab_discount_formatted:
        service.tab_discount && tab_discount?.length > 0
          ? tab_discount.map((discount) => ({
              ...discount,
              max_discount: formatterCurrency(discount?.max_discount / 100),
            }))
          : [],
    };
  };

  const getServicesData = async (page) => {
    try {
      setIsLoadingData(true);
      const response = await getServices({
        search: searchInput || "",
        limit: LIMIT_PER_PAGE,
        page: page,
        filters: filterString,
      });

      setCurrentPage(page);

      setPagination(
        paginate(
          response.total_items,
          LIMIT_PER_PAGE,
          Math.ceil(response.total_items / response.limit)
        )
      );

      if (!response?.data || response?.data?.length === 0) {
        throw new Error("No services found");
      }

      const formattedServices = response.data.map((service) =>
        formatService(service)
      );

      setServices(formattedServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const updateServiceStatus = async () => {
    try {
      setIsUpdatingData(true);
      const editedService = await updateService(serviceToEdit.id, {
        status: serviceToEdit.status,
      });
      setServices(
        services.map((service) => {
          if (service.id === editedService.id) {
            return {
              ...service,
              status_formatted: (
                <StatusMenuButton
                  status={editedService.status}
                  customStatus={["active", "disabled"]}
                  updateStatus={(newStatus) => {
                    if (newStatus === editedService.status) return;
                    setShowModal(true);
                    setServiceToEdit({ id: service.id, status: newStatus });
                  }}
                />
              ),
            };
          }
          return service;
        })
      );
      setAlert({
        active: true,
        type: "success",
        message: "Servicio actualizado correctamente",
      });
    } catch (error) {
      console.log({ error });
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setServiceToEdit(null);
      setIsUpdatingData(false);
      setShowModal(false);
    }
  };

  const addService = (service) => {
    setServices((prev) => [formatService(service), ...prev]);
  };

  const editService = (serviceToEdit) => {
    const updatedServices = services.map((service) => {
      if (service.id === serviceToEdit.id) {
        return formatService(serviceToEdit);
      }
      return service;
    });
    setServices(updatedServices);
  };

  useEffect(() => {
    getServicesData(1);
    return () => setServices([]);
  }, [filterString]);

  useEffect(() => {
    getServicesData(currentPage);
    return () => setServices([]);
  }, [currentPage]);

  useEffect(() => {
    searchInput !== null && getServicesData(1);
    return () => setServices([]);
  }, [searchInput]);

  return {
    services,
    formatService,
    isLoadingData,
    isUpdatingData,
    updateServiceStatus,
    addService,
    editService,
    serviceToEdit,
    setServiceToEdit,
    showModal,
    setShowModal,
    currentPage,
    setCurrentPage,
    pagination,
  };
}
